import { useEffect, useState } from "react";
import client, { getErrorMessage } from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import SiteDetailPanel from "./SiteDetailPanel";
import SiteFormDialog from "./SiteFormDialog";
import { toast } from "sonner";
import { Search, Plus } from "lucide-react";
import { PaginationBar } from "@/components/shared/PaginationBar";

const MANAGE_ROLES = ["Director", "Manager", "Back Office"];

export default function SiteListPage() {
  const { user } = useAuth();
  const [sites, setSites] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("Active");
  const [selected, setSelected] = useState(null);
  const [formTarget, setFormTarget] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const canManage = MANAGE_ROLES.includes(user?.role);

  const load = (pageNum = 1) => {
    setLoading(true);
    const params = { include_inactive: statusFilter === "All", page: pageNum, page_size: 50 };
    if (search.trim()) params.search = search.trim();
    client
      .get("/site-master/sites", { params })
      .then((res) => {
        setSites(res.data.items);
        setTotal(res.data.total);
        setPage(res.data.page);
        setTotalPages(res.data.total_pages);
      })
      .catch((err) => toast.error(getErrorMessage(err)))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    const t = setTimeout(() => load(1), 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, statusFilter]);

  const handleSaved = (data) => {
    setFormTarget(null);
    setSelected(data);
    load(page);
  };

  return (
    <div className="space-y-6" data-testid="site-master-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>
            Site Master
          </h1>
          <p className="text-sm text-slate-500" data-testid="site-count">
            {total} site record{total === 1 ? "" : "s"}
          </p>
        </div>
        {canManage && (
          <Button data-testid="site-add-button" onClick={() => setFormTarget("new")} className="bg-amber-600 hover:bg-amber-700">
            <Plus className="h-4 w-4 mr-1.5" /> Add Site
          </Button>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative w-full max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input data-testid="site-search-input" placeholder="Search by Site ID, client or label" className="pl-9" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <div className="flex gap-2">
          {["Active", "All"].map((s) => (
            <button
              key={s}
              data-testid={`site-status-filter-${s.toLowerCase()}`}
              onClick={() => setStatusFilter(s)}
              className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors ${
                statusFilter === s ? "bg-amber-600 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
              }`}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
        <Table data-testid="site-table">
          <TableHeader>
            <TableRow>
              <TableHead>Site ID</TableHead>
              <TableHead>Display Label</TableHead>
              <TableHead>Client</TableHead>
              <TableHead>State</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Site Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-8 text-slate-400">
                  Loading...
                </TableCell>
              </TableRow>
            ) : sites.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-8 text-slate-400">
                  No sites found.
                </TableCell>
              </TableRow>
            ) : (
              sites.map((s) => (
                <TableRow key={s.id} data-testid={`site-row-${s.site_id || s.code}`} className="cursor-pointer" onClick={() => setSelected(s)}>
                  <TableCell className="font-mono text-xs font-semibold">{s.site_id || "—"}</TableCell>
                  <TableCell className="font-medium">{s.display_label || s.label}</TableCell>
                  <TableCell>{s.client_short_name || "—"}</TableCell>
                  <TableCell>{s.state || "—"}</TableCell>
                  <TableCell>{s.job_work_type || "—"}</TableCell>
                  <TableCell>
                    {s.active === false ? (
                      <Badge className="bg-slate-400">Deactivated</Badge>
                    ) : s.site_status ? (
                      <Badge className={s.site_status === "Active" ? "bg-emerald-600" : "bg-slate-400"}>{s.site_status}</Badge>
                    ) : (
                      <span className="text-slate-400 text-sm">Not set</span>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <PaginationBar page={page} totalPages={totalPages} onPageChange={load} testId="site-pagination" />

      {selected && (
        <SiteDetailPanel
          site={selected}
          onClose={() => setSelected(null)}
          canManage={canManage}
          onEdit={(s) => {
            setSelected(null);
            setFormTarget(s);
          }}
          onUpdated={(updated) => {
            setSelected(updated);
            setSites((prev) => prev.map((s) => (s.id === updated.id ? updated : s)));
          }}
        />
      )}

      {formTarget && <SiteFormDialog site={formTarget === "new" ? null : formTarget} onClose={() => setFormTarget(null)} onSaved={handleSaved} />}
    </div>
  );
}
